import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Input,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@devhop/ui";
import { Download, Send } from "lucide-react";
import type React from "react";
import type {
  FormDetailDTO,
  FormFieldPreviewDTO,
} from "@/modules/document-portal/domain/contracts";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import { PrintDocumentShell } from "@/shared/ui/PrintDocumentShell";

type FormTemplateProps = {
  data: FormDetailDTO;
  formValues: Record<string, unknown>;
  setFormValues: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  notes: string;
  setNotes: React.Dispatch<React.SetStateAction<string>>;
  onDownloadPrintPdf: () => void;
  onSubmitForm: () => void;
  isSubmitting: boolean;
};

export function FormTemplate({
  data,
  formValues,
  setFormValues,
  notes,
  setNotes,
  onDownloadPrintPdf,
  onSubmitForm,
  isSubmitting,
}: FormTemplateProps) {
  const printableRows = data.fields.map((field) => ({
    id: field.id,
    label: field.label,
    value: stringifyPrintValue(formValues[field.id]),
    hint: field.hint,
    isRequired: field.isRequired,
  }));

  const printFooterParts = [
    data.version && `ເວີຊັນ ${data.version}`,
    data.categoryName,
  ]
    .filter(Boolean)
    .join(" | ");

  return (
    <>
      <div className="mb-6 grid gap-4 lg:grid-cols-2">
        <Card>
          <CardContent className="space-y-3 p-4 py-0">
            <h2 className="font-semibold text-base">ຂໍ້ມູນທົ່ວໄປ</h2>
            {data.description && (
              <p className="text-muted-foreground text-sm">
                {data.description}
              </p>
            )}
            {data.useCase && (
              <div>
                <p className="font-medium text-muted-foreground text-xs">
                  ການນຳໃຊ້
                </p>
                <p className="text-sm">{data.useCase}</p>
              </div>
            )}
            {data.targetAudience && (
              <div>
                <p className="font-medium text-muted-foreground text-xs">
                  ກຸ່ມເປົ້າໝາຍ
                </p>
                <p className="text-sm">{data.targetAudience}</p>
              </div>
            )}
            {data.processAfterSubmission && (
              <div>
                <p className="font-medium text-muted-foreground text-xs">
                  ຂັ້ນຕອນຫຼັງສົ່ງ
                </p>
                <p className="text-sm">{data.processAfterSubmission}</p>
              </div>
            )}
            <p className="text-muted-foreground text-xs">
              ແນບໄຟລ໌: {data.allowFileAttachment ? "ອະນຸຍາດ" : "ບໍ່ອະນຸຍາດ"}
            </p>
            {data.versionChangelog && (
              <div>
                <p className="font-medium text-muted-foreground text-xs">
                  ບັນທຶກການປ່ຽນແປງ
                </p>
                <p className="text-sm">{data.versionChangelog}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 py-0">
            <h2 className="mb-3 font-semibold text-base">ຕົວຢ່າງ / ໄຟລ໌ດາວໂຫລດ</h2>
            {data.examples.length === 0 ? (
              <p className="text-muted-foreground text-sm">ບໍ່ມີຕົວຢ່າງໄຟລ໌</p>
            ) : (
              <ul className="space-y-3">
                {data.examples.map((ex) => (
                  <li
                    key={ex.id}
                    className="flex flex-col gap-2 rounded-lg border border-border p-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div>
                      <p className="font-medium text-sm">{ex.title}</p>
                      {ex.description && (
                        <p className="text-muted-foreground text-xs">
                          {ex.description}
                        </p>
                      )}
                      {ex.fileKey && (
                        <p className="mt-1 truncate text-muted-foreground text-xs">
                          {ex.fileKey}
                        </p>
                      )}
                    </div>
                    {ex.fileKey ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="shrink-0 border-black"
                        onClick={() => {
                          const downloadUrl = resolveImageSrc(ex.fileKey);
                          if (!downloadUrl) return;
                          const link = document.createElement("a");
                          link.href = downloadUrl;
                          link.target = "_blank";
                          link.rel = "noopener noreferrer";
                          link.download = ex.title || "form-sample";
                          link.click();
                        }}
                      >
                        <Download className="mr-1 size-4" />
                        ດາວໂຫລດ
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
      <Card className="mt-6 print:hidden">
        <CardContent className="space-y-4 p-4 py-0">
          <h2 className="font-semibold text-base">ຕື່ມແບບຟອມດິຈິຕອລ</h2>
          {data.fields.length === 0 ? (
            <p className="text-muted-foreground text-sm">ບໍ່ມີຊ່ອງກອກຂໍ້ມູນ</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {data.fields.map((field) => (
                <div
                  key={field.id}
                  className={
                    field.fieldType === "textarea" ||
                    field.fieldType === "multiselect" ||
                    field.fieldType === "signature"
                      ? "sm:col-span-2"
                      : ""
                  }
                >
                  <label
                    htmlFor={`form-field-${field.id}`}
                    className="mb-1 block font-medium text-sm"
                  >
                    {field.label}
                    {field.isRequired ? (
                      <span className="ml-1 text-destructive">*</span>
                    ) : null}
                  </label>
                  {renderFieldInput(
                    field,
                    formValues[field.id],
                    `form-field-${field.id}`,
                    (value) =>
                      setFormValues((prev) => ({ ...prev, [field.id]: value })),
                  )}
                  {field.hint ? (
                    <p className="mt-1 text-muted-foreground text-xs">
                      {field.hint}
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          )}

          <div>
            <label
              htmlFor="form-notes"
              className="mb-1 block font-medium text-sm"
            >
              ໝາຍເຫດ
            </label>
            <Textarea
              id="form-notes"
              className="min-h-24 w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-ring focus:ring-2 focus:ring-ring/20"
              placeholder="ລາຍລະອຽດເພີ່ມເຕີມ..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <div
            data-tourid="doc-form-detail-submit"
            className="flex flex-wrap gap-2"
          >
            <Button
              type="button"
              variant="outline"
              className="border-black"
              onClick={onDownloadPrintPdf}
            >
              <Download className="mr-2 size-4" />
              ດາວໂຫລດ / ພິມ
            </Button>
            <Button
              type="button"
              className="bg-black text-white hover:bg-black/90"
              onClick={onSubmitForm}
              disabled={isSubmitting}
            >
              <Send className="mr-2 size-4" />
              {isSubmitting ? "ກຳລັງສົ່ງ..." : "ສົ່ງແບບຟອມ"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="hidden print:block">
        <PrintDocumentShell
          rootId="form-submission-pdf-content"
          title={data.name}
          extraFooterLine={printFooterParts || undefined}
        >
          <p className="mb-6 text-gray-600 text-sm">
            ສະແດງຂໍ້ມູນເພື່ອດາວໂຫລດເປັນ PDF / ພິມ
          </p>
          <div className="space-y-3">
            {printableRows.map((row) => (
              <div
                key={row.id}
                className="rounded border border-gray-300 px-3 py-2"
              >
                <p className="font-medium text-gray-800 text-sm">
                  {row.label}
                  {row.isRequired ? " *" : ""}
                </p>
                <p className="text-gray-900 text-sm">{row.value || "—"}</p>
                {row.hint ? (
                  <p className="mt-1 text-gray-600 text-xs">{row.hint}</p>
                ) : null}
              </div>
            ))}
          </div>
          {notes.trim() ? (
            <div className="mt-4 rounded border border-gray-300 px-3 py-2">
              <p className="font-medium text-gray-800 text-sm">ໝາຍເຫດ</p>
              <p className="text-gray-900 text-sm">{notes}</p>
            </div>
          ) : null}
        </PrintDocumentShell>
      </div>
    </>
  );
}

function stringifyPrintValue(v: unknown): string {
  if (v == null) return "";
  if (v instanceof File) return v.name;
  if (typeof v === "string") return v;
  if (typeof v === "number") return String(v);
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.join(", ");
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function renderFieldInput(
  field: FormFieldPreviewDTO,
  value: unknown,
  inputId: string,
  onChange: (value: unknown) => void,
) {
  const options = normalizeOptions(field.options);
  const strValue = typeof value === "string" ? value : "";
  const baseClassName =
    "w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-ring focus:ring-2 focus:ring-ring/20";

  switch (field.fieldType) {
    case "textarea":
      return (
        <Textarea
          id={inputId}
          className={`${baseClassName} min-h-24`}
          value={strValue}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "date":
      return (
        <Input
          id={inputId}
          type="date"
          className={baseClassName}
          value={strValue}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "datetime":
      return (
        <Input
          id={inputId}
          type="datetime-local"
          className={baseClassName}
          value={strValue}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "time":
      return (
        <Input
          id={inputId}
          type="time"
          className={baseClassName}
          value={strValue}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "email":
      return (
        <Input
          id={inputId}
          type="email"
          autoComplete="email"
          className={baseClassName}
          value={strValue}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "phone":
      return (
        <Input
          id={inputId}
          type="tel"
          autoComplete="tel"
          className={baseClassName}
          value={strValue}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "select":
      return (
        <Select value={strValue} onValueChange={onChange}>
          <SelectTrigger id={inputId}>
            <SelectValue placeholder="ເລືອກ..." />
          </SelectTrigger>
          <SelectContent>
            {options.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    case "radio":
      return (
        <RadioGroup
          value={strValue}
          onValueChange={onChange}
          className="grid gap-2"
        >
          {options.map((opt) => (
            <label
              key={opt.value}
              htmlFor={`${inputId}-${opt.value}`}
              className="inline-flex items-center gap-2 text-sm"
            >
              <RadioGroupItem
                id={`${inputId}-${opt.value}`}
                value={opt.value}
              />
              {opt.label}
            </label>
          ))}
        </RadioGroup>
      );
    case "multiselect": {
      const selected = Array.isArray(value)
        ? value.filter((x): x is string => typeof x === "string")
        : [];
      return (
        <div className="grid gap-2">
          {options.map((opt) => (
            <label
              key={opt.value}
              htmlFor={`${inputId}-${opt.value}`}
              className="inline-flex items-center gap-2 text-sm"
            >
              <Checkbox
                id={`${inputId}-${opt.value}`}
                checked={selected.includes(opt.value)}
                onCheckedChange={(checked) => {
                  const next = checked
                    ? [...selected, opt.value]
                    : selected.filter((x) => x !== opt.value);
                  onChange(next);
                }}
              />
              {opt.label}
            </label>
          ))}
        </div>
      );
    }
    case "number":
    case "money":
      return (
        <Input
          id={inputId}
          type="number"
          step={field.fieldType === "money" ? "0.01" : "1"}
          className={baseClassName}
          value={
            typeof value === "number" || typeof value === "string"
              ? String(value)
              : ""
          }
          onChange={(e) =>
            onChange(e.target.value === "" ? "" : Number(e.target.value))
          }
        />
      );
    case "file": {
      const selected = value instanceof File ? value : null;
      return (
        <div className="space-y-2">
          <input
            type="file"
            id={inputId}
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              onChange(f ?? null);
              e.target.value = "";
            }}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => document.getElementById(inputId)?.click()}
            >
              ເລືອກໄຟລ໌
            </Button>
            {selected ? (
              <span className="text-muted-foreground text-sm">
                {selected.name}
              </span>
            ) : null}
          </div>
          <p className="text-muted-foreground text-xs">
            ໄຟລ໌ຈະຖືກອັບໂຫຼດໄປຫາ object store ເມື່ອກົດສົ່ງແບບຟອມ
          </p>
        </div>
      );
    }
    case "checkbox":
      return (
        <label
          htmlFor={inputId}
          className="inline-flex items-center gap-2 text-sm"
        >
          <Checkbox
            id={inputId}
            checked={Boolean(value)}
            onCheckedChange={(v) => onChange(Boolean(v))}
          />
          ຢືນຢັນ
        </label>
      );
    case "signature":
      return (
        <Textarea
          id={inputId}
          className={`${baseClassName} min-h-20`}
          placeholder="ລາຍເຊັນ ຫຼື ຂໍ້ຄວາມຢືນຢັນ"
          value={strValue}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    default:
      return (
        <Input
          id={inputId}
          type="text"
          className={baseClassName}
          value={strValue}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
}

function normalizeOptions(
  options: unknown,
): Array<{ label: string; value: string }> {
  if (!Array.isArray(options)) return [];
  return options
    .map((opt) => {
      if (typeof opt === "string") return { label: opt, value: opt };
      if (opt && typeof opt === "object") {
        const value = (opt as { value?: unknown }).value;
        const label = (opt as { label?: unknown }).label;
        if (typeof value === "string" && typeof label === "string") {
          return { label, value };
        }
        if (typeof value === "string") {
          return { label: value, value };
        }
      }
      return null;
    })
    .filter((o): o is { label: string; value: string } => Boolean(o));
}
